//
// Copyright 2022 DXOS.org
//

import * as Reactivity from '@effect/experimental/Reactivity';
import type * as SqlClient from '@effect/sql/SqlClient';
import * as Effect from 'effect/Effect';
import * as Layer from 'effect/Layer';
import * as ManagedRuntime from 'effect/ManagedRuntime';

import { Trigger } from '@dxos/async';
import { DEFAULT_WORKER_BROADCAST_CHANNEL } from '@dxos/client-protocol';
import { type Config } from '@dxos/config';
import { Context } from '@dxos/context';
import { invariant } from '@dxos/invariant';
import { log } from '@dxos/log';
import {
  MemorySignalManager,
  MemorySignalManagerContext,
  WebsocketSignalManager,
  setIdentityTags,
} from '@dxos/messaging';
import { RtcTransportProxyFactory } from '@dxos/network-manager';
import { type RpcPort } from '@dxos/rpc';
import * as SqlExport from '@dxos/sql-sqlite/SqlExport';
import * as SqliteClient from '@dxos/sql-sqlite/SqliteClient';
import * as SqlTransaction from '@dxos/sql-sqlite/SqlTransaction';
import { type MaybePromise } from '@dxos/util';

import { ClientServicesHost } from '../services';
import { WorkerSession } from './worker-session';

// NOTE: Keep as RpcPorts to avoid dependency on @dxos/rpc-tunnel so we don't depend on browser-specific apis.
export type CreateSessionProps = {
  appPort: RpcPort;
  systemPort: RpcPort;
  shellPort?: RpcPort;
  onClose?: () => Promise<void>;
};

export type WorkerRuntimeOptions = {
  channel?: string;
  configProvider: () => MaybePromise<Config>;
  acquireLock: () => Promise<void>;
  releaseLock: () => void;
  onStop?: () => Promise<void>;
  /**
   * @default true
   */
  automaticallyConnectWebrtc?: boolean;

  /**
   * Optional SQLite layer for Effect. Defaults to LocalSqliteOpfsLayer.
   * For testing in Node.js, use `sqliteLayerMemory` from `@dxos/sql-sqlite/platform`.
   */
  sqliteLayer?: Layer.Layer<SqlClient.SqlClient | SqlExport.SqlExport, unknown>;
};

/**
 * Runtime for the shared and dedciated worker.
 * Manages connections from proxies (in tabs).
 * Tabs make requests to the `ClientServicesHost`, and provide a WebRTC gateway.
 */
export class WorkerRuntime {
  private readonly _configProvider: () => MaybePromise<Config>;
  private readonly _acquireLock: () => Promise<void>;
  private readonly _releaseLock: () => void;
  private readonly _onStop?: () => Promise<void>;
  private readonly _transportFactory = new RtcTransportProxyFactory();
  private readonly _ready = new Trigger<Error | undefined>();
  private readonly _sessions = new Set<WorkerSession>();
  private readonly _clientServices!: ClientServicesHost;
  private readonly _channel: string;
  private readonly _automaticallyConnectWebrtc: boolean;
  private readonly _livenessLock = new WebLockWrapper(`@dxos/client-services/WorkerRuntime/${crypto.randomUUID()}`);
  private _broadcastChannel?: BroadcastChannel;
  private _sessionForNetworking?: WorkerSession; // TODO(burdon): Expose to client QueryStatusResponse.
  private _config!: Config;
  private _signalMetadataTags: any = { runtime: 'worker-runtime' };
  private _signalTelemetryEnabled: boolean = false;
  private _runtime!: ManagedRuntime.ManagedRuntime<
    SqlTransaction.SqlTransaction | SqlClient.SqlClient | SqlExport.SqlExport,
    never
  >;

  constructor({
    channel = DEFAULT_WORKER_BROADCAST_CHANNEL,
    configProvider,
    acquireLock,
    releaseLock,
    onStop,
    automaticallyConnectWebrtc = true,
    sqliteLayer,
  }: WorkerRuntimeOptions) {
    this._configProvider = configProvider;
    this._acquireLock = acquireLock;
    this._releaseLock = releaseLock;
    this._onStop = onStop;
    this._channel = channel;
    if (sqliteLayer) {
      log.warn('Using testing SQLite layer');
    }
    this._runtime = ManagedRuntime.make(
      SqlTransaction.layer
        .pipe(Layer.provideMerge(sqliteLayer ?? LocalSqliteOpfsLayer), Layer.provideMerge(Reactivity.layer))
        .pipe(Layer.orDie),
    );
    this._clientServices = new ClientServicesHost({
      callbacks: {
        onReset: async () => this.stop(),
      },
      runtime: this._runtime.runtimeEffect,
      runtimeProps: {
        // Auto-activate spaces that were previously active after leader changeover.
        autoActivateSpaces: true,
      },
    });
    this._automaticallyConnectWebrtc = automaticallyConnectWebrtc;
  }

  get host() {
    return this._clientServices;
  }

  get livenessLockKey(): string {
    return this._livenessLock.key;
  }

  async start(): Promise<void> {
    log('starting...');
    try {
      log('worker-runtime: acquiring liveness lock (background)');
      void this._livenessLock.acquire();

      // Steal the lock from the other worker.
      log('worker-runtime: broadcasting stop to displace previous worker');
      this._broadcastChannel = new BroadcastChannel(this._channel);
      this._broadcastChannel.postMessage({ action: 'stop' });
      this._broadcastChannel.onmessage = async (event) => {
        if (event.data?.action === 'stop') {
          log('worker-runtime: received stop broadcast');
          await this.stop();
        }
      };

      log('worker-runtime: acquiring storage lock');
      await this._acquireLock();
      log('worker-runtime: storage lock acquired, resolving config');
      this._config = await this._configProvider();
      log('worker-runtime: config resolved');
      this._signalTelemetryEnabled = this._config.get('runtime.client.signalTelemetryEnabled') ?? false;
      const observabilityGroup = this._config.get('runtime.client.observabilityGroup');
      if (observabilityGroup) {
        this._signalMetadataTags.group = observabilityGroup;
      }
      const signals = this._config.get('runtime.services.signaling');
      log('worker-runtime: initializing client services host');
      this._clientServices.initialize({
        config: this._config,
        signalManager: this._config.get('runtime.client.edgeFeatures')?.signaling
          ? undefined
          : signals
            ? new WebsocketSignalManager(signals, () => (this._signalTelemetryEnabled ? this._signalMetadataTags : {}))
            : new MemorySignalManager(new MemorySignalManagerContext()), // TODO(dmaretskyi): Inject this context.
        transportFactory: this._transportFactory,
      });
      log('worker-runtime: client services host initialized, opening');

      await this._clientServices.open(new Context());
      log('worker-runtime: client services host opened, signalling ready');
      this._ready.wake(undefined);
      log('started');
      setIdentityTags({
        identityService: this._clientServices.services.IdentityService!,
        devicesService: this._clientServices.services.DevicesService!,
        setTag: (k: string, v: string) => {
          this._signalMetadataTags[k] = v;
        },
      });
    } catch (err: any) {
      this._ready.wake(err);
      log.error('starting', err);
    }
  }

  async stop(): Promise<void> {
    // Release the lock to notify remote clients that the worker is terminating.
    this._releaseLock();
    this._broadcastChannel?.close();
    this._broadcastChannel = undefined;
    await this._clientServices.close(Context.default());
    await this._runtime.dispose();
    await this._onStop?.();
    await this._livenessLock.release();
  }

  /**
   * Update signaling telemetry tags from a client-supplied config overlay.
   *
   * The worker services outlive individual client connections, so the first client seeds the
   * worker's core config (storage, signaling, edge features). For fields that can legitimately
   * differ per tab — `observabilityGroup` and `signalTelemetryEnabled` — this method lets later
   * connections refresh the signal metadata the worker attaches to its signaling requests
   * (last-writer-wins, matching the pre-DX-930 per-session RPC behaviour).
   */
  updateSignalMetadata(config: Config): void {
    const observabilityGroup = config.get('runtime.client.observabilityGroup');
    if (observabilityGroup) {
      this._signalMetadataTags.group = observabilityGroup;
    } else {
      // Clear stale group so a later config that removes observabilityGroup stops attributing
      // telemetry to the previous client's group (last-writer-wins).
      delete this._signalMetadataTags.group;
    }
    const signalTelemetryEnabled = config.get('runtime.client.signalTelemetryEnabled');
    if (signalTelemetryEnabled !== undefined) {
      this._signalTelemetryEnabled = signalTelemetryEnabled;
    }
  }

  /**
   * Create a new session.
   */
  async createSession({ appPort, systemPort, shellPort, onClose }: CreateSessionProps): Promise<WorkerSession> {
    const session = new WorkerSession({
      serviceHost: this._clientServices,
      appPort,
      systemPort,
      shellPort,
      readySignal: this._ready,
    });

    // When tab is closed or client is destroyed.
    session.onClose.set(async () => {
      this._sessions.delete(session);
      if (this._sessions.size === 0) {
        // Terminate the worker when all sessions are closed.
        await this.stop();
      } else {
        if (this._automaticallyConnectWebrtc) {
          this._reconnectWebrtc();
        }
      }
      await onClose?.();
    });

    await session.open();
    // A worker can only service one origin currently
    invariant(
      !this._signalMetadataTags.origin || this._signalMetadataTags.origin === session.origin,
      `worker origin changed from ${this._signalMetadataTags.origin} to ${session.origin}?`,
    );
    this._signalMetadataTags.origin = session.origin;
    this._sessions.add(session);

    if (this._automaticallyConnectWebrtc) {
      this._reconnectWebrtc();
    }

    return session;
  }

  /**
   * Connects the WebRTC bridge to the specified session.
   * If no session is provided, disconnects the WebRTC bridge.
   *
   * Called automatically if `automaticallyConnectWebrtc` is true.
   *
   * @param session The session to connect the WebRTC bridge to.
   */
  connectWebrtcBridge(session: WorkerSession | undefined): void {
    this._sessionForNetworking = session;
    this._transportFactory.setBridgeService(session?.bridgeService);
  }

  /**
   * Selects one of the existing session for WebRTC networking.
   */
  private _reconnectWebrtc(): void {
    log('reconnecting webrtc...');
    // Check if current session is already closed.
    if (this._sessionForNetworking) {
      if (!this._sessions.has(this._sessionForNetworking)) {
        this._sessionForNetworking = undefined;
      }
    }

    // Select existing session.
    if (!this._sessionForNetworking) {
      const selected = Array.from(this._sessions).find((session) => session.bridgeService);
      this.connectWebrtcBridge(selected);
    }
  }
}

const DB_NAME = 'DXOS';

/**
 * SqlExport layer that wraps SqliteClient to provide export functionality.
 */
const SqlExportLayer: Layer.Layer<SqlExport.SqlExport, never, SqliteClient.SqliteClient> = Layer.effect(
  SqlExport.SqlExport,
  Effect.gen(function* () {
    const sql = yield* SqliteClient.SqliteClient;
    return {
      export: sql.export,
    } satisfies SqlExport.Service;
  }),
);

/**
 * Local SQLite layer for the worker.
 * Uses in-process OPFS via {@link SqliteClient.layerOpfs} (no MessagePort).
 * NOTE: Only usable within a worker.
 */
const LocalSqliteOpfsLayer = SqlExportLayer.pipe(
  Layer.provideMerge(SqliteClient.layerOpfs({ dbName: DB_NAME })),
  Layer.provideMerge(Reactivity.layer),
);

// TODO(wittjosiah): Factor out to a separate module.
class WebLockWrapper {
  readonly #key: string;
  #release?: () => void;

  constructor(key: string) {
    this.#key = key;
  }

  get key(): string {
    return this.#key;
  }

  acquire(options: LockOptions = {}) {
    return navigator.locks.request(this.#key, options, async () => {
      await new Promise<void>((resolve) => {
        this.#release = resolve;
      }); // Blocks for the duration of the worker's lifetime.
      this.#release = undefined;
    });
  }

  release() {
    this.#release?.();
    this.#release = undefined;
  }

  [Symbol.dispose]() {
    this.release();
  }
}
